// Timezone utilities
export interface TimezoneOption {
  value: string;
  label: string;
  group: string;
}

/**
 * Get today's date in YYYY-MM-DD format using local timezone
 * Avoids timezone offset issues with Date.toISOString()
 */
export const getLocalDateString = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse a date string (YYYY-MM-DD) to a Date object in local timezone
 */
export const parseLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Common timezones grouped by region
export const TIMEZONES: TimezoneOption[] = [
  // Americas
  { value: 'America/New_York', label: 'Eastern Time (ET)', group: 'Americas' },
  { value: 'America/Chicago', label: 'Central Time (CT)', group: 'Americas' },
  { value: 'America/Denver', label: 'Mountain Time (MT)', group: 'Americas' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', group: 'Americas' },
  { value: 'America/Phoenix', label: 'Arizona Time (MST)', group: 'Americas' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)', group: 'Americas' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)', group: 'Americas' },
  { value: 'America/Toronto', label: 'Toronto (ET)', group: 'Americas' },
  { value: 'America/Vancouver', label: 'Vancouver (PT)', group: 'Americas' },
  { value: 'America/Mexico_City', label: 'Mexico City (CST)', group: 'Americas' },
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)', group: 'Americas' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires (ART)', group: 'Americas' },
  
  // Europe
  { value: 'Europe/London', label: 'London (GMT/BST)', group: 'Europe' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)', group: 'Europe' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)', group: 'Europe' },
  { value: 'Europe/Rome', label: 'Rome (CET/CEST)', group: 'Europe' },
  { value: 'Europe/Madrid', label: 'Madrid (CET/CEST)', group: 'Europe' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam (CET/CEST)', group: 'Europe' },
  { value: 'Europe/Stockholm', label: 'Stockholm (CET/CEST)', group: 'Europe' },
  { value: 'Europe/Zurich', label: 'Zurich (CET/CEST)', group: 'Europe' },
  { value: 'Europe/Vienna', label: 'Vienna (CET/CEST)', group: 'Europe' },
  { value: 'Europe/Warsaw', label: 'Warsaw (CET/CEST)', group: 'Europe' },
  { value: 'Europe/Athens', label: 'Athens (EET/EEST)', group: 'Europe' },
  { value: 'Europe/Moscow', label: 'Moscow (MSK)', group: 'Europe' },
  { value: 'Europe/Istanbul', label: 'Istanbul (TRT)', group: 'Europe' },
  
  // Asia
  { value: 'Asia/Dubai', label: 'Dubai (GST)', group: 'Asia' },
  { value: 'Asia/Kolkata', label: 'Mumbai/New Delhi (IST)', group: 'Asia' },
  { value: 'Asia/Bangkok', label: 'Bangkok (ICT)', group: 'Asia' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)', group: 'Asia' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)', group: 'Asia' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)', group: 'Asia' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)', group: 'Asia' },
  { value: 'Asia/Seoul', label: 'Seoul (KST)', group: 'Asia' },
  { value: 'Asia/Jakarta', label: 'Jakarta (WIB)', group: 'Asia' },
  { value: 'Asia/Manila', label: 'Manila (PHT)', group: 'Asia' },
  { value: 'Asia/Taipei', label: 'Taipei (CST)', group: 'Asia' },
  
  // Oceania
  { value: 'Australia/Sydney', label: 'Sydney (AEDT/AEST)', group: 'Oceania' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEDT/AEST)', group: 'Oceania' },
  { value: 'Australia/Brisbane', label: 'Brisbane (AEST)', group: 'Oceania' },
  { value: 'Australia/Perth', label: 'Perth (AWST)', group: 'Oceania' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZDT/NZST)', group: 'Oceania' },
  
  // Africa
  { value: 'Africa/Cairo', label: 'Cairo (EET)', group: 'Africa' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)', group: 'Africa' },
  { value: 'Africa/Lagos', label: 'Lagos (WAT)', group: 'Africa' },
  { value: 'Africa/Nairobi', label: 'Nairobi (EAT)', group: 'Africa' },
];

export const getTimezoneGroups = (): Record<string, TimezoneOption[]> => {
  const groups: Record<string, TimezoneOption[]> = {};
  TIMEZONES.forEach(tz => {
    if (!groups[tz.group]) {
      groups[tz.group] = [];
    }
    groups[tz.group].push(tz);
  });
  return groups;
};

export const getCurrentTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'America/New_York'; // Fallback
  }
};

export const findTimezoneOption = (timezone: string): TimezoneOption | undefined => {
  return TIMEZONES.find(tz => tz.value === timezone);
};

